import { useCallback, useEffect, useState } from 'react';
import { api } from '@halo/web-lib';
import { Activity, ExternalLink, RefreshCw } from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

interface DashboardLink {
  name: string;
  url: string;
}

interface DashboardsResponse {
  dashboards: DashboardLink[];
}

interface SeriesPoint {
  timestamp: string;
  value: number;
}

interface SeriesResponse {
  metricType: string;
  points: SeriesPoint[];
}

interface MetricSpec {
  metricType: string;
  label: string;
}

const METRICS: readonly MetricSpec[] = [
  {
    metricType: 'custom.googleapis.com/halo/ingestion/api_calls_total',
    label: 'API calls (total)',
  },
  {
    metricType: 'custom.googleapis.com/halo/ingestion/rows_written_total',
    label: 'Rows written (total)',
  },
  {
    metricType: 'run.googleapis.com/request_count',
    label: 'Cloud Run requests',
  },
] as const;

const REFETCH_MS = 60_000;

/* ─── Sparkline ───────────────────────────────── */

interface SparklineStats {
  min: number;
  max: number;
  avg: number;
  latest: number;
}

function computeStats(points: SeriesPoint[]): SparklineStats | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return null;
  let min = first.value;
  let max = first.value;
  let sum = 0;
  for (const p of points) {
    if (p.value < min) min = p.value;
    if (p.value > max) max = p.value;
    sum += p.value;
  }
  return {
    min,
    max,
    avg: sum / points.length,
    latest: last.value,
  };
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) {
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (Number.isInteger(n)) return n.toString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function Sparkline({ points }: { points: SeriesPoint[] }) {
  const width = 200;
  const height = 40;

  if (points.length === 0) {
    return (
      <div className="w-[200px] h-[40px] flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-600">
        no data
      </div>
    );
  }

  if (points.length === 1) {
    return (
      <svg width={width} height={height} className="block">
        <circle cx={width / 2} cy={height / 2} r={2} className="fill-gray-900 dark:fill-gray-100" />
      </svg>
    );
  }

  const first = points[0];
  if (!first) {
    return (
      <div className="w-[200px] h-[40px] flex items-center justify-center text-[10px] text-gray-400 dark:text-gray-600">
        no data
      </div>
    );
  }
  let min = first.value;
  let max = first.value;
  for (const p of points) {
    if (p.value < min) min = p.value;
    if (p.value > max) max = p.value;
  }
  const range = max - min || 1;

  const coords = points.map((p, idx) => {
    const x = (idx / (points.length - 1)) * width;
    const y = height - ((p.value - min) / range) * height;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      <polyline
        points={coords.join(' ')}
        fill="none"
        strokeWidth={1.5}
        className="stroke-gray-900 dark:stroke-gray-100"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ─── Metric Card ─────────────────────────────── */

interface MetricCardProps {
  spec: MetricSpec;
}

function MetricCard({ spec }: MetricCardProps) {
  const [points, setPoints] = useState<SeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSeries = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get<SeriesResponse>(
        `/api/v1/ingestion/admin/monitoring/series?metricType=${encodeURIComponent(spec.metricType)}&minutesBack=60`,
      );
      if (res.success && res.data) {
        setPoints(res.data.points ?? []);
      } else {
        setError('Failed to load metric series.');
      }
    } catch (err) {
      console.error('Failed to fetch series:', err);
      setError('Failed to load metric series.');
    } finally {
      setLoading(false);
    }
  }, [spec.metricType]);

  useEffect(() => {
    fetchSeries();
    const interval = setInterval(fetchSeries, REFETCH_MS);
    return () => clearInterval(interval);
  }, [fetchSeries]);

  const stats = computeStats(points);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{spec.label}</div>
          <div
            className="text-[10px] font-mono text-gray-400 dark:text-gray-600 mt-0.5 truncate max-w-[200px]"
            title={spec.metricType}
          >
            {spec.metricType}
          </div>
        </div>
      </div>

      <div className="mt-3 mb-3">
        {loading ? (
          <div className="w-[200px] h-[40px] bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
        ) : error ? (
          <div className="w-[200px] h-[40px] flex items-center justify-center text-[10px] text-red-500">
            {error}
          </div>
        ) : (
          <Sparkline points={points} />
        )}
      </div>

      <div className="grid grid-cols-4 gap-2 text-[10px]">
        <div>
          <div className="text-gray-400 dark:text-gray-600 uppercase tracking-wide">Min</div>
          <div className="font-mono text-gray-900 dark:text-gray-100">{stats ? formatNumber(stats.min) : '—'}</div>
        </div>
        <div>
          <div className="text-gray-400 dark:text-gray-600 uppercase tracking-wide">Max</div>
          <div className="font-mono text-gray-900 dark:text-gray-100">{stats ? formatNumber(stats.max) : '—'}</div>
        </div>
        <div>
          <div className="text-gray-400 dark:text-gray-600 uppercase tracking-wide">Avg</div>
          <div className="font-mono text-gray-900 dark:text-gray-100">{stats ? formatNumber(stats.avg) : '—'}</div>
        </div>
        <div>
          <div className="text-gray-400 dark:text-gray-600 uppercase tracking-wide">Latest</div>
          <div className="font-mono text-gray-900 dark:text-gray-100">{stats ? formatNumber(stats.latest) : '—'}</div>
        </div>
      </div>
    </div>
  );
}

/* ─── Dashboard Cards ─────────────────────────── */

function DashboardCards() {
  const [dashboards, setDashboards] = useState<DashboardLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboards = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get<DashboardsResponse>('/api/v1/ingestion/admin/monitoring/dashboards');
      if (res.success && res.data) {
        setDashboards(res.data.dashboards ?? []);
      } else {
        setError('Failed to load dashboards.');
      }
    } catch (err) {
      console.error('Failed to fetch dashboards:', err);
      setError('Failed to load dashboards.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 rounded-xl p-4 text-sm">
        {error}
      </div>
    );
  }

  if (dashboards.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 text-center text-xs text-gray-400 dark:text-gray-600">
        No dashboards configured.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {dashboards.map((d) => (
        <a
          key={d.name}
          href={d.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm transition group"
        >
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{d.name}</div>
              <div className="text-[10px] text-gray-400 dark:text-gray-600 mt-1">Open in Cloud Monitoring</div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600 group-hover:text-gray-700 dark:group-hover:text-gray-300 shrink-0 ml-2" />
          </div>
        </a>
      ))}
    </div>
  );
}

/* ─── Main Component ──────────────────────────── */

export function MonitoringPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Monitoring
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Cloud Monitoring deep-links and live metric sparklines (last 60 minutes).
          </p>
        </div>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Deep-link cards */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-2">
          Dashboards
        </div>
        <DashboardCards key={`dash-${refreshKey}`} />
      </div>

      {/* Sparklines */}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-2">
          Live metrics
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {METRICS.map((spec) => (
            <MetricCard key={`${spec.metricType}-${refreshKey}`} spec={spec} />
          ))}
        </div>
      </div>
    </div>
  );
}
